import { Button, Spinner } from "react-bootstrap";
import type { TileState } from "@/lib/analytics/tileState";
import { tierPlaceholder, type WidgetTier } from "@/lib/analytics/widgetRegistry";

/** The parts of an S08 tile state that a non-data body prints. */
export type QueryStateTile = Pick<TileState, "body" | "message" | "detail" | "queryId" | "actions">;

type QueryStateProps = {
  tile: QueryStateTile;
  /** Names the tile in its Retry button. */
  label: string;
  /** Retry is offered only with a handler. */
  onRetry?: () => void;
  /** The Timeout state's "Last 7 days" action; offered only with a handler. */
  onSetRange?: () => void;
  /** A 403 on a tier-gated region renders the tier placeholder (S09 AC7). */
  tierGate?: { viewer: WidgetTier; needed: WidgetTier };
};

const TierPlaceholder = ({ viewer, needed }: { viewer: WidgetTier; needed: WidgetTier }) => {
  const { title, viewerTier, neededTier } = tierPlaceholder(viewer, needed);
  return (
    <>
      <p className="fw-semibold mb-1">{title}</p>
      <dl className="mb-0">
        <div className="pz-kv">
          <dt>Your tier</dt>
          <dd>{viewerTier}</dd>
        </div>
        <div className="pz-kv">
          <dt>Needed</dt>
          <dd>{neededTier}</dd>
        </div>
      </dl>
    </>
  );
};

/**
 * A tile's non-data body (CMP-11.11, ANL-E04-S09): Loading, or the state's message, detail, query
 * id and actions, as the S08 selector chose them. Shared by `AnalyticsTile` and `MetricCard`.
 */
const QueryState = ({ tile, label, onRetry, onSetRange, tierGate }: QueryStateProps) => {
  if (tile.body === "loading") {
    return (
      <div role="status" className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" aria-hidden="true" />
        <span>{tile.message}</span>
      </div>
    );
  }
  if (tile.body === "forbidden" && tierGate) {
    return <TierPlaceholder viewer={tierGate.viewer} needed={tierGate.needed} />;
  }
  return (
    <>
      <p className="fw-semibold mb-1">{tile.message}</p>
      {tile.detail ? <p className="mb-1">{tile.detail}</p> : null}
      {tile.queryId ? (
        <p className="small text-muted mb-0">
          Query ID <code>{tile.queryId}</code>
        </p>
      ) : null}
      {tile.actions.map((action) => {
        if (action.kind === "retry") {
          return onRetry ? (
            <Button
              key="retry"
              variant="outline-danger"
              size="sm"
              className="mt-2"
              aria-label={`${action.label} ${label}`}
              onClick={onRetry}
            >
              {action.label}
            </Button>
          ) : null;
        }
        return onSetRange ? (
          <Button key="set-range" variant="outline-secondary" size="sm" className="mt-2" onClick={onSetRange}>
            {action.label}
          </Button>
        ) : null;
      })}
    </>
  );
};

export default QueryState;
